import React, {useCallback, useEffect, useMemo, useReducer} from "react";

import {Flex, View} from "@adobe/react-spectrum";

import {useGetAnalysisReport, useGetColumns} from "../../../../services/awesome-api";
import {ActionType, initialState, reducer, State} from "./State";
import {DateRangePickerOnChange} from "../../../components/DateRangePicker";
import {FiltersPropsOnChange} from "../../../components/Filters";
import {MultiSelectOnChange} from "../../../components/MultiSelect";
import {IntervalPickerOnChange} from "../../../components/IntervalPicker";
import Sidebar from "./Sidebar";
import Main from "./Main/Main";
import {AnalysisViewTypePickerOnChange} from "./Main/AnalysisViewTypePicker";
import {AnalysisEntityPickerOnChange} from "../../../components/AnalysisEntityPicker";
import {useAuth} from "../../../../services/auth";
import {toISODate} from "../../../../utils/date";

function AnalysisScreen() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        useGetIdToken,
    } = useAuth();

    const token = useGetIdToken() || '';

    const {
        data: columns,
        error: getColumnsError,
        isFetching: isFetchingColumns,
        refetch: fetchColumns,
    } = useGetColumns({
        entity: state.analysisEntity,
        token,
    })

    useEffect(() => {
        if (token !== '') {
            fetchColumns()
        }
    }, [
        fetchColumns,
        token,
    ])

    const queryRules = useMemo(() => {
        return state.ruleGroup.rules.map((rule) => ({
            value: rule.expression,
            operator: rule.operator,
            field: rule.field.name,
            //    TODO factor out. See the same in DataScreen
        })).concat([
            {
                value: toISODate(state.dateRange.startDate),
                operator: "greater than or equal to",
                field: "date",
            }
        ]).concat([
            {
                value: toISODate(state.dateRange.endDate),
                operator: "less than or equal to",
                field: "date",
            }
        ])
    }, [
        state.dateRange,
        state.ruleGroup.rules,
    ]);

    const {
        data: fetchReportResponse,
        error: fetchReportError,
        isFetching: isFetchingReport,
        refetch: fetchReport,
    } = useGetAnalysisReport({
        columns: state.selectedColumns.map((column) => column.value),
        rule_group: {
            rules: queryRules
        },
        interval: state.interval,
        entity: state.analysisEntity,
        token,
    })

    useEffect(() => {
        // Calculate isReadyToFetchLeads
        let isReadyToFetchLeads = true;
        const reasons: State["isNotReadyToFetchLeadsReasons"] = [];

        if (token === '') {
            isReadyToFetchLeads = false;
        } else if (isFetchingColumns || isFetchingReport) {
            isReadyToFetchLeads = false;
        }

        dispatch({
            type: ActionType.SET_IS_READY_TO_FETCH_LEADS,
            payload: {
                isReadyToFetchLeads,
                reasons,
            }
        })
    }, [
        isFetchingColumns,
        isFetchingReport,
        token,
    ]);

    const dateRangePickerOnChange: DateRangePickerOnChange = useCallback((dateRange) => {
        dispatch({
            type: ActionType.SET_DATE_RANGE,
            payload: {dateRange},
        })
    }, [])

    const filtersPropsOnChange: FiltersPropsOnChange = useCallback((rules) => {
        dispatch({
            type: ActionType.SET_RULE_GROUP,
            payload: {ruleGroup: {rules}}
        })
    }, [])

    const columnsOnChange: MultiSelectOnChange = useCallback((columns) => {
        dispatch({
            type: ActionType.SET_SELECTED_COLUMNS,
            payload: {columns}
        })
    }, [])

    const intervalPickerOnChange: IntervalPickerOnChange = useCallback((interval) => {
        dispatch({
            type: ActionType.SET_INTERVAL,
            payload: {interval}
        })
    }, [])

    const analysisViewTypePickerOnChange: AnalysisViewTypePickerOnChange = useCallback((viewType) => {
        dispatch({
            type: ActionType.SET_ANALYSIS_VIEW_TYPE,
            payload: {viewType}
        })
    }, [])

    const analysisEntityPickerOnChange: AnalysisEntityPickerOnChange = useCallback((entity) => {
        dispatch({
            type: ActionType.SET_ANALYSIS_ENTITY,
            payload: {entity}
        })
    }, [])

    return (
        <View
            UNSAFE_className="unscrollable"
        >
            <Flex
                direction="column"
                UNSAFE_className="unscrollable"
            >
                <Flex
                    gap="size-300"
                    UNSAFE_className="unscrollable"
                >
                    <Sidebar
                        isFetchingColumns={isFetchingColumns}
                        getColumnsError={getColumnsError}
                        columns={columns}
                        dateRangePickerOnChange={dateRangePickerOnChange}
                        filtersPropsOnChange={filtersPropsOnChange}
                        columnsOnChange={columnsOnChange}
                        intervalPickerOnChange={intervalPickerOnChange}
                        dateRange={state.dateRange}
                        selectedColumns={state.selectedColumns}
                        filterRules={state.ruleGroup.rules}
                        interval={state.interval}
                        analysisEntity={state.analysisEntity}
                        analysisEntityPickerOnChange={analysisEntityPickerOnChange}
                    />

                    <Main
                        fetchReportError={fetchReportError}
                        isFetchingReport={isFetchingReport}
                        fetchReport={fetchReport}
                        fetchReportResponse={fetchReportResponse}
                        state={state}
                        analysisViewTypePickerOnChange={analysisViewTypePickerOnChange}
                    />

                </Flex>
            </Flex>
        </View>
    )
}

export default AnalysisScreen
